import { PaymentInstructionOrigin } from '../app/features/manual-payments/models/payment-instruction-origin';
import { AccountTypeIdEnum } from '../app/models/enum/shared/account-type-id.enum';

export class Routes {
  // Auth
  static signIn(): string {
    return `auth/sign-in`;
  }

  static signOut(): string {
    return `auth/sign-out`;
  }

  static forgotPassword(): string {
    return `auth/forgot-password`;
  }

  static accessRestricted(): string {
    return `auth/access-restricted`;
  }

  static dashboard(): string {
    return `/dashboard`;
  }

  static accounts(): string {
    return `/accounts`;
  }

  static profile(): string {
    return `/profile`;
  }

  // Employer Portal
  static epAllEmployees(): string {
    return `/employees`;
  }

  static epAddEmployee(): string {
    return `/employees/add-employee`;
  }

  static epAddEmployees(): string {
    return `/employees/add-employees`;
  }

  static epEmployeeDetails(memberId: number): string {
    return `/employees/${memberId}`;
  }

  static epViewEmployerUser(employerUserId: number): string {
    return `/settings/employer-users/${employerUserId}`;
  }

  static epEmployerRecord(): string {
    return `/settings/employer-record`;
  }

  static epEmployerRecordUpdateContacts(): string {
    return `/settings/employer-record/update-contacts`;
  }

  static epRemittances(): string {
    return `/remittances/employers/remittances`;
  }

  static epEmployerSubmissionDetails(submissionId: number): string {
    return `/settings/employer-record/submissions/${submissionId}`;
  }

  static epViewDesignatedUnit(planId: number, unitId: string): string {
    return `settings/employer-record/plan/${planId}/units/${unitId}`;
  }

  static epEmployerUsers(): string {
    return `/settings/employer-users`;
  }

  // Internal Portal
  static ipAllMembers(): string {
    return `/records/members`;
  }

  static ipAllEmployers(): string {
    return `/records/employers`;
  }

  static ipAllInternalUsers(): string {
    return `/settings/internal-users`;
  }

  static ipViewInternalUser(internalUserId: number): string {
    return `/settings/internal-users/${internalUserId}`;
  }

  static ipAddEmployer(): string {
    return `/records/employers/add-employer`;
  }

  static ipAddMultipleMembers(): string {
    return `/records/add-members`;
  }

  static ipAddNonMember(): string {
    return `/records/add-non-member`;
  }

  static ipMemberAccount(memberId: number, accountId: number): string {
    return `/records/members/${memberId}/accounts/${accountId}`;
  }

  static ipMemberRecord(memberId: number): string {
    return `/records/members/${memberId}`;
  }

  static ipMemberRecordAccountInvestmentInstructions(memberId?: number): string {
    return `/records/members/${memberId}/accounts/instructions`;
  }

  static ipMemberRecordAccountFundAllocation(memberId?: number): string {
    return `/records/members/${memberId}/accounts/allocation`;
  }

  static ipCreateFiduciary(memberId: number): string {
    return `/records/members/${memberId}/fiduciaries/create`;
  }

  static ipEditFiduciary(memberId: number): string {
    return `/records/members/${memberId}/fiduciaries/edit`;
  }

  static ipSpousalWaivers(memberId: number): string {
    return `/records/members/${memberId}/spousal-waivers`;
  }

  static ipUpdateAccountTypeBeneficiaries(memberId: number, accountTypeId: number): string {
    return `/records/members/${memberId}/accounts/${accountTypeId}/beneficiaries`;
  }

  static ipEmployerRecord(employerId: number) {
    return `/records/employers/${employerId}`;
  }

  static ipEmployerUserDetails(employerId: number, employerUserId: number): string {
    return `/records/employers/${employerId}/employer-users/${employerUserId}`;
  }

  static ipEmployerRecordUpdateContacts(employerId: number): string {
    return `/records/employers/${employerId}/update-contacts`;
  }

  static ipViewDesignatedUnit(employerId: number, planId: number, unitId: string): string {
    return `/records/employers/${employerId}/plan/${planId}/units/${unitId}`;
  }

  static ipEditRemittance(remittanceId: number): string {
    return `/remittances/${remittanceId}/edit`;
  }

  static ipProcessRemittance(remittanceId: number): string {
    return `/remittances/${remittanceId}/process`;
  }

  static ipCreateRemittance(): string {
    return `/remittances/create`;
  }

  static ipRemittanceQueue(): string {
    return `/remittances/queue`;
  }

  static ipViewEmployerRemittances(employerId: number): string {
    return `/remittances/employers/${employerId}/remittances`;
  }

  static ipCreateEmployerRemittance(employerId: number): string {
    return `/remittances/create/${employerId}`;
  }

  static ipViewTransaction(transactionId: number, remittanceId: number): string {
    return `/transactions/${transactionId}/remittance/${remittanceId}`;
  }

  static ipViewSubmissions(): string {
    return `/submissions`;
  }

  static ipViewMemberSubmissionDetails(submissionId: number): string {
    return `/submissions/member/${submissionId}`;
  }

  static ipViewEmployerSubmissionDetails(submissionId: number): string {
    return `/submissions/employer/${submissionId}`;
  }

  static ipMemberVariableBenefitConfiguration(memberId: number): string {
    return `/records/members/${memberId}/variable-benefit-configuration`;
  }

  static ipMemberUpdateVariableBenefitConfiguration(memberId: number): string {
    return `/records/members/${memberId}/update-variable-benefit-configuration`;
  }

  static ipMemberVbPaymentChange(memberId: number): string {
    return `/records/members/${memberId}/change-variable-benefit-payments`;
  }

  static ipCreateMemberMonthlyPensionAccount(memberId: number): string {
    return `/records/members/${memberId}/create-monthly-pension-account`;
  }

  static ipSplitPension(memberId: number, accountId: number, instructionId: string): string {
    return `/records/members/${memberId}/accounts/${accountId}/payment-instructions/${instructionId}/split`;
  }

  static ipTransferInRequest(memberId: number): string {
    return `/records/members/${memberId}/transfer-in-request`;
  }

  static ipEmploymentStatusChange(memberId: number, employerId: number): string {
    return `/records/members/${memberId}/employment-status-change/${employerId}`;
  }

  static ipAccounting(): string {
    return `/accounting`;
  }

  static ipManualPayments(): string {
    return `/accounting/manual-payments`;
  }

  static ipInboundPayments(): string {
    return `/accounting/inbound-payments`;
  }

  static ipInboundBatch(batchId: number): string {
    return `/accounting/inbound-payments/${batchId}`;
  }

  static ipOutboundPayments(): string {
    return `/accounting/outbound-payments`;
  }

  static ipPaymentBatch(batchId: number): string {
    return `/accounting/outbound-payments/${batchId}`;
  }

  static ipNewManualPaymentInstruction(origin: PaymentInstructionOrigin): string {
    return `/accounting/manual-payments/new/${origin.type}/${origin.id}`;
  }

  static ipManualPaymentInstruction(instructionId: number): string {
    return `/accounting/manual-payments/${instructionId}`;
  }

  static ipAlerts(): string {
    return `/settings/alerts`;
  }

  static ipAlertDetails(alertId: number): string {
    return `/settings/alerts/${alertId}`;
  }

  static ipViewReleaseNote(releaseNoteId: number): string {
    return `/settings/release-notes/view/${releaseNoteId}`;
  }

  static ipEditReleaseNote(releaseNoteId: number): string {
    return `/settings/release-notes/edit/${releaseNoteId}`;
  }

  static ipRoles(): string {
    return `/settings/roles`;
  }

  // Member Portal
  static mpAccountInvestmentInstructions(
    accountTypeId: AccountTypeIdEnum = AccountTypeIdEnum.DefinedContribution
  ): string {
    return `/accounts/instructions/${accountTypeId}`;
  }

  static mpAccountFundAllocation(accountTypeId: AccountTypeIdEnum = AccountTypeIdEnum.DefinedContribution): string {
    return `/accounts/allocation/${accountTypeId}`;
  }

  static mpUpdateAccountTypeBeneficiaries(accountTypeId: number): string {
    return `/accounts/${accountTypeId}/beneficiaries`;
  }

  static mpSetInvestmentInstructions(accountTypeId: AccountTypeIdEnum = AccountTypeIdEnum.DefinedContribution): string {
    return `/accounts/instructions/${accountTypeId}`;
  }

  static mpViewSubmissions(): string {
    return `/profile/member-submissions`;
  }

  static mpViewSubmission(submissionId: number): string {
    return `/profile/member-submissions/${submissionId}`;
  }

  static mpViewAccount(accountId: number): string {
    return `/accounts/${accountId}`;
  }

  static mpVbPaymentChange(): string {
    return `/accounts/change-variable-benefit-payments`;
  }

  static mpTransferInRequest(): string {
    return `/accounts/transfer-in-request`;
  }
}
